import { authGuard, AuthGmsClientCallbackComponent, canActivateRoleGuard } from '@coin/shared/auth/feature';
import { environmentGuard } from '@coin/shared/core/feature-guards';
import { NoBackendScreenComponent } from '@coin/shared/core/feature-legacy-components';
import { FrontendType, PermissionAction, PermissionResource } from '@coin/shared/core/util-enums';
import { CoinRoutes } from '@coin/shared/core/util-models';

export const APP_ROUTES: CoinRoutes = [
  {
    path: 'auth/callback',
    component: AuthGmsClientCallbackComponent
  },
  {
    path: '',
    loadChildren: () => import('./home/home.routes'),
    canActivate: [authGuard],
    data: {
      skipFrontendTypeCheckForRoles: [
        PermissionResource.SuccessionManagementAdmin,
        PermissionResource.OrganisationManagementAdmin,
        PermissionResource.OrganisationPlanningAdmin,
        PermissionResource.HeadcountApprovalAdmin,
        PermissionResource.ApprovalWorkflowsAdmin,
        PermissionResource.JobAdTemplateAdmin
      ],
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'logout',
    loadChildren: () => import('./log-out/log-out.routes')
  },
  {
    path: 'pdfcreate',
    loadChildren: () => import('./pdf-create/pdf-create.routes')
  },
  {
    path: 'season-mgmt',
    loadChildren: () => import('./season-mgmt/season-mgmt.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.Seasons
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'translations',
    loadChildren: () => import('./translations/translations.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.Translations
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'cms',
    loadChildren: () => import('./cms/cms.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        },
        {
          resource: PermissionResource.Templates
        },
        {
          resource: PermissionResource.CMS
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'master-data',
    loadChildren: () => import('./master-data/master-data.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.GeneralCluster
        },
        {
          resource: PermissionResource.Mercer
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'communication',
    loadChildren: () => import('./communication/communication.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.Communication
        },
        {
          resource: PermissionResource.Templates,
          actions: [PermissionAction.All]
        }
      ])
    ],
    data: {
      title: 'home.communication',
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'user-mgmt',
    loadChildren: () => import('./user-mgmt/user-mgmt.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.UserManagement
        },
        {
          resource: PermissionResource.RoleManagement
        }
      ])
    ],
    data: {
      skipFrontendTypeCheckForRoles: [PermissionResource.OrganisationPlanningAdmin],
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'incentive',
    loadChildren: () => import('./incentive/incentive.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.KPIList
        },
        {
          resource: PermissionResource.KPIValues
        },
        {
          resource: PermissionResource.Factors
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'org-planning',
    loadChildren: () => import('./org-planning/org-planning.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationPlanningAdmin
        }
      ])
    ],
    data: {
      skipFrontendTypeCheckForRoles: [PermissionResource.OrganisationPlanningAdmin],
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'org-management',
    loadChildren: () => import('./org-management/org-management.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.OrganisationManagementAdmin
        }
      ])
    ],
    data: {
      featureFlag: 'orgManagement',
      frontendType: FrontendType.admin,
      skipFrontendTypeCheckForRoles: [PermissionResource.OrganisationManagementAdmin]
    }
  },
  {
    path: 'cosmos',
    loadChildren: () => import('./cosmos/cosmos.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.CosmosAdmin
        },
        {
          resource: PermissionResource.OrganisationTags
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'employment-conditions-library',
    loadChildren: () => import('./employment-conditions-library/employment-conditions-library.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.EmploymentConditionLibraryAdmin
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryRestricted
        },
        {
          resource: PermissionResource.EmploymentConditionLibraryGenerator
        },
        {
          resource: PermissionResource.SignatureAuthorizationAdmin
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'restructuring',
    loadChildren: () => import('./restructuring/restructuring.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.RestructuringAdmin
        },
        {
          resource: PermissionResource.RestructuringAdminRestricted
        }
      ])
    ],
    data: {
      title: 'home.restructuring',
      featureFlag: 'restructuring',
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'succession-management',
    loadChildren: () => import('./succession-management/succession-management.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.SuccessionManagementAdmin
        }
      ])
    ],
    data: {
      skipFrontendTypeCheckForRoles: [PermissionResource.SuccessionManagementAdmin],
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'interfaces',
    loadChildren: () => import('./interfaces/interfaces.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'headcount',
    loadChildren: () => import('./headcount/headcount.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        },
        {
          resource: PermissionResource.HeadcountApprovalAdmin
        },
        {
          resource: PermissionResource.ApprovalWorkflowsAdmin
        },
        {
          resource: PermissionResource.JobAdTemplateAdmin
        }
      ])
    ],
    data: {
      frontendType: FrontendType.admin,
      skipFrontendTypeCheckForRoles: [
        PermissionResource.HeadcountApprovalAdmin,
        PermissionResource.ApprovalWorkflowsAdmin,
        PermissionResource.JobAdTemplateAdmin
      ]
    }
  },
  {
    path: 'api-tests',
    loadChildren: () => import('./api-test-page/api-test-page.routes'),
    canActivate: [
      authGuard,
      canActivateRoleGuard([
        {
          resource: PermissionResource.All
        }
      ])
    ],
    canMatch: [environmentGuard],
    data: {
      featureFlag: 'apiTestPage',
      frontendType: FrontendType.admin
    }
  },
  {
    path: 'unauthorized',
    loadChildren: () => import('./unauthorized/unauthorized.routes')
  },
  {
    path: 'fallback',
    component: NoBackendScreenComponent
  },
  {
    path: '**',
    loadChildren: () => import('./not-found/not-found.routes')
  }
];
